import { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const ThemeContext = createContext(null);

const LEGACY_STORAGE_KEY = 'gym-crm-theme';

function getPortalScope(pathname = '') {
  if (pathname.startsWith('/owner')) return 'owner';
  if (pathname.startsWith('/admin') || pathname.startsWith('/receptionist')) return 'staff';
  return 'public';
}

function themeStorageKey(scope) {
  return `gym-crm-theme-${scope}`;
}

function readTheme(scope) {
  if (typeof window === 'undefined') return 'dark';
  return window.sessionStorage.getItem(themeStorageKey(scope)) || 'dark';
}

function writeTheme(scope, theme) {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(themeStorageKey(scope), theme);
  // Keep portals isolated — never write a shared localStorage theme key
  window.localStorage.removeItem(LEGACY_STORAGE_KEY);
}

function applyThemeToDocument(theme) {
  document.documentElement.setAttribute('data-theme', theme);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', theme === 'dark' ? '#0F1218' : '#f1f5f9');
  }
}

export function ThemeProvider({ children }) {
  const location = useLocation();
  const scope = getPortalScope(location.pathname);
  const [theme, setTheme] = useState(() => readTheme(getPortalScope(window.location.pathname)));

  // Load this portal's theme when switching owner ↔ admin/receptionist ↔ public
  useEffect(() => {
    // Logout lands on public auth — keep the panel light/dark preference
    if (scope === 'public') {
      const staffTheme = window.sessionStorage.getItem(themeStorageKey('staff'));
      const ownerTheme = window.sessionStorage.getItem(themeStorageKey('owner'));
      const inherited = staffTheme || ownerTheme;
      if (inherited === 'light' || inherited === 'dark') {
        setTheme(inherited);
        applyThemeToDocument(inherited);
        writeTheme('public', inherited);
        return;
      }
    }

    const next = readTheme(scope);
    setTheme(next);
    applyThemeToDocument(next);
  }, [scope]);

  useEffect(() => {
    applyThemeToDocument(theme);
    writeTheme(scope, theme);
  }, [theme, scope]);

  const toggleTheme = () => {
    setTheme((current) => (current === 'dark' ? 'light' : 'dark'));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, themeScope: scope }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
